import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight, Circle } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import * as React from 'react'

import { cn } from '../lib/utils'

// The panel's motion is driven by `motion`, not CSS keyframes. Both can express the same
// curve, but only one is INTERRUPTIBLE: a CSS keyframe restarts from its first frame when
// re-triggered, so a fast open → close → open snaps, where motion picks up from wherever the
// panel currently is. That difference is why the hand-rolled switcher this replaced felt
// better than the CSS port of it, and it can't be closed by matching easings.
//
// Values are the ones that switcher used. `easeOut` here is motion's own — cubic-bezier(0, 0,
// 0.58, 1) — which is NOT Tailwind's `ease-out` (0, 0, 0.2, 1); the two diverge by up to 0.20
// in progress, and the CSS port was visibly snappier off the mark for it.
const PANEL_HIDDEN = { opacity: 0, scale: 0.95, y: -8 }
const PANEL_SHOWN = { opacity: 1, scale: 1, y: 0 }
const PANEL_TRANSITION = { duration: 0.15, ease: 'easeOut' } as const

// Radix owns the open state, but `DropdownMenuContent` needs it to drive `AnimatePresence`,
// and Radix exposes no hook for it. Root mirrors it into context — controlled or not — so the
// content can mount/unmount on its own schedule while Radix keeps managing everything else.
const DropdownMenuOpenContext = React.createContext(false)

function DropdownMenu({ open, defaultOpen, onOpenChange, children, ...props }: DropdownMenuPrimitive.DropdownMenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false)
  const isControlled = open !== undefined
  const isOpen = isControlled ? open : uncontrolledOpen

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  return (
    <DropdownMenuOpenContext.Provider value={isOpen}>
      <DropdownMenuPrimitive.Root open={isOpen} onOpenChange={handleOpenChange} {...props}>
        {children}
      </DropdownMenuPrimitive.Root>
    </DropdownMenuOpenContext.Provider>
  )
}

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

// The WAI-ARIA menu-button pattern says Tab closes the menu and moves focus to the next
// element in the page's tab sequence. Radix traps Tab inside the menu instead — in both
// modal and non-modal mode — so a keyboard user has to press Escape before they can carry
// on through the page. Restored here.
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusSiblingOf(el: HTMLElement, backwards: boolean) {
  // `el` itself is kept even while the menu is mid-teardown, so its index stays findable.
  const all = Array.from(document.querySelectorAll<HTMLElement>(TABBABLE)).filter((n) => n === el || n.offsetParent !== null)
  const index = all.indexOf(el)
  if (index === -1) return
  all[index + (backwards ? -1 : 1)]?.focus()
}

// Menu items carry the focus indicator as a leading accent bar rather than a background
// alone: the tinted background is ~1.07:1 against the menu surface, which is far too faint
// to be the only cue. The bar is the same pelorous the rest of the app focuses with.
const MENU_ITEM_FOCUS =
  'relative before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:rounded-full before:bg-pelorous-500 before:opacity-0 before:transition-opacity focus:before:opacity-100 dark:before:bg-pelorous-300'

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      'flex cursor-default select-none items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
      MENU_ITEM_FOCUS,
      inset && 'pl-8',
      className
    )}
    {...props}>
    {children}
    <ChevronRight className="ml-auto" />
  </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      // Same motion as DropdownMenuContent — including the slide-OUT half, which a submenu
      // needs for the same reason the root panel does.
      'z-50 min-w-[8rem] overflow-hidden rounded-2xl border bg-popover p-2 text-popover-foreground shadow-card-hover',
      'origin-[--radix-dropdown-menu-content-transform-origin] [animation-timing-function:cubic-bezier(0,0,0.58,1)]',
      'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
      'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
      'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      'data-[side=bottom]:slide-out-to-top-2 data-[side=left]:slide-out-to-right-2 data-[side=right]:slide-out-to-left-2 data-[side=top]:slide-out-to-bottom-2',
      // Reduced motion: see the note on DropdownMenuContent for why this needs the `!`.
      'motion-reduce:!animate-none',
      className
    )}
    {...props}
  />
))
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, onKeyDownCapture, children, ...props }, ref) => {
  const open = React.useContext(DropdownMenuOpenContext)
  const reduced = useReducedMotion()

  return (
    // `forceMount` hands presence to AnimatePresence: Radix would otherwise drop the panel the
    // instant it closes, leaving no exit to animate. Radix still owns everything else —
    // positioning, the dismiss layer, focus, typeahead.
    <AnimatePresence>
      {open && (
        <DropdownMenuPrimitive.Portal forceMount>
          <DropdownMenuPrimitive.Content
            ref={ref}
            forceMount
            asChild
            sideOffset={sideOffset}
            onKeyDownCapture={(event) => {
              onKeyDownCapture?.(event)
              if (event.key !== 'Tab' || event.defaultPrevented) return
              // Capture phase, so this lands before Radix's own handler swallows the key.
              event.preventDefault()
              const content = event.currentTarget
              // Resolve the trigger via aria-controls (keyed off the content's own generated id)
              // rather than aria-labelledby: a call site that puts its own `id` on the trigger
              // overrides Radix's, which leaves aria-labelledby pointing at nothing.
              const trigger = content.id ? document.querySelector<HTMLElement>(`[aria-controls="${CSS.escape(content.id)}"]`) : null
              const backwards = event.shiftKey
              // Close through Escape so Radix runs its normal teardown (dismiss layer, focus
              // restore) rather than us reaching into its internals.
              content.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
              if (!trigger) return
              // Radix restores focus to the trigger only once the close animation finishes, so
              // waiting a frame loses the race. Step off the moment that restore actually lands.
              const stepOff = () => {
                // Re-entrant focus() inside a focus handler is unreliable; take the next tick.
                setTimeout(() => focusSiblingOf(trigger, backwards), 0)
              }
              trigger.addEventListener('focus', stepOff, { once: true })
              // Don't leave the listener armed if the restore never comes.
              setTimeout(() => trigger.removeEventListener('focus', stepOff), 1000)
            }}
            {...props}>
            <motion.div
              initial={reduced ? false : PANEL_HIDDEN}
              animate={PANEL_SHOWN}
              exit={reduced ? { opacity: 0 } : PANEL_HIDDEN}
              transition={reduced ? { duration: 0 } : PANEL_TRANSITION}
              className={cn(
                // shadow-card-hover, not Tailwind's shadow-lg: the DS overlay shadows are
                // brand-tinted with a deep, soft falloff (52px blur here), where shadow-lg is a
                // tight neutral-black 15px. The panel reads as genuinely lifted rather than
                // stuck to the page — and it is what the hand-rolled switcher used.
                'z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-2xl border bg-popover p-2 text-popover-foreground shadow-card-hover',
                // The panel grows out of the corner nearest its trigger and retracts back into
                // it. Origin comes from Radix, so it tracks side/align automatically.
                'origin-[--radix-dropdown-menu-content-transform-origin]',
                className
              )}>
              {children}
            </motion.div>
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      )}
    </AnimatePresence>
  )
})
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'flex cursor-default select-none items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0',
      MENU_ITEM_FOCUS,
      inset && 'pl-8',
      className
    )}
    {...props}
  />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'flex cursor-default select-none items-center rounded-[10px] py-2 pl-8 pr-2.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      MENU_ITEM_FOCUS,
      className
    )}
    checked={checked}
    {...props}>
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'flex cursor-default select-none items-center rounded-[10px] py-2 pl-8 pr-2.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      MENU_ITEM_FOCUS,
      className
    )}
    {...props}>
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
))
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label ref={ref} className={cn('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)} {...props} />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator ref={ref} className={cn('-mx-2 my-1.5 h-px bg-border', className)} {...props} />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup
}
